import { $, $$, aviso, avisoHtml, cajaDelActo, el, esc, limpiar, misCajas, noContesto, noHabilitado, nombreCaja, num, pedir, puede, puedoActo, puedoOperar, rpc, rpcLee, sb } from './nucleo';
import { ERROR_PEDIDOS, PEDIDOS, TOPE_PEDIDOS, cargarPedidos, leyendoPedidos, pedidosCortados } from './pedidos';
import { cargarCaja } from './caja';

// =====================================================================
//  COBROS  (8a′)
//  La pantalla no decide nada: muestra lo que dice `v_pedidos` y llama a
//  `registrar_cobro` / `anular_cobro`. Cuánto hay que cobrar, en qué caja
//  cae y si se puede, lo resuelve la base.
// =====================================================================
export let MEDIOS = [], MONEDAS = [], COTIZ = {}, MONEDA_CAT = 'USD';

//  Lo que NO se pudo leer, que no es lo mismo que lo que no hay. Son CUATRO
//  banderas porque cierran puertas distintas (regla 11); las usan cobros, Caja,
//  Cuentas, Vueltas y Comisiones.
//  Ninguna relación se tapa por permiso (RLS = `app.es_usuario()`): un `error`
//  acá es siempre una falla de verdad, no hay nada que preguntarle a `puede()`.
export let ERROR_MEDIOS = '',         //  `medios_pago`: con qué se cobra
           ERROR_MONEDAS_COBRO = '',  //  `monedas`: en qué moneda se cobra
           ERROR_COTIZ_COBRO = '',    //  las cotizaciones: cuánto vale hoy
           ERROR_MONEDA_CAT = '';     //  `config`: en qué moneda está expresado todo
//  La quinta: la promesa entera RECHAZÓ (el `await` tira, no hay `error`). Se
//  convierte en bandera acá, donde pasan las cinco pantallas, en vez de pedirle
//  un `try` a cada una.
export let ERROR_CATALOGOS = '';

export async function cargarCatalogosCobro(){
  //  OJO, regla 32: estas cuatro NO van por `pedir()`. La promesa tiene un
  //  `.catch` propio; si las lecturas dejaran de rechazar, `ERROR_CATALOGOS` no
  //  se pondría nunca y `rotuloMoneda()` escribiría "USD" sobre una moneda que
  //  nadie leyó.
  const [med, mon, cot, cfg] = await Promise.all([
    sb.from('medios_pago').select('id,codigo,nombre').eq('activo',true).order('orden'),
    sb.from('monedas').select('id,codigo,nombre,decimales,es_base').eq('activo',true).order('orden'),
    sb.from('v_cotizaciones_vigentes').select('codigo,valor'),
    sb.from('config').select('clave,valor').eq('clave','precio.moneda')
  ]);
  ERROR_MEDIOS        = med.error ? med.error.message : '';
  ERROR_MONEDAS_COBRO = mon.error ? mon.error.message : '';
  ERROR_COTIZ_COBRO   = cot.error ? cot.error.message : '';
  ERROR_MONEDA_CAT    = cfg.error ? cfg.error.message : '';
  MEDIOS = med.data || [];
  MONEDAS = mon.data || [];
  COTIZ = Object.fromEntries((cot.data||[]).map(c => [c.codigo, Number(c.valor)]));
  //  Con `config` ilegible vuelve al respaldo 'USD', que es una suposición: lo
  //  que se MUESTRA sale de `rotuloMoneda()` y lo que se puede HACER, de `frenoPlata()`.
  MONEDA_CAT = String((cfg.data||[])[0]?.valor ?? 'USD');
}

//  El rótulo de la moneda de catálogo. Si no se pudo leer, escribir "USD" sería
//  afirmar algo que nadie leyó.
//  Ojo: mira las DOS banderas. Cuando la promesa rechaza, `ERROR_MONEDA_CAT`
//  queda en '' y `MONEDA_CAT` en el respaldo; con sólo la primera se pintaría
//  "USD" (Comisiones no consulta ningún freno).
export const rotuloMoneda = () =>
  (ERROR_CATALOGOS || ERROR_MONEDA_CAT) ? '(no se sabe la moneda)' : MONEDA_CAT;

//  Lo que impide tocar plata, o '' si no impide nada. Devuelve HTML (mensaje
//  ya escapado con esc()): se pinta con avisoHtml(), nunca con aviso().
//
//  Cierran LA MISMA puerta —registrar plata— y se preguntan juntas: sin saber
//  con qué se cobra y en qué moneda, el número es una adivinanza. Las
//  cotizaciones no están acá: las decide `frenoCotizacion()`.
//  Son DOS frenos porque la bandera tiene que ser tan fina como la puerta
//  (regla 11).
//
//  `frenoMonedas()`: bolsín y gasto, donde sólo se elige moneda y monto.
export function frenoMonedas(){
  //  Primero el rechazo: si la promesa reventó, las banderas quedaron en ''
  //  y "no se pudieron leer las monedas" nombraría la consulta equivocada.
  if (ERROR_CATALOGOS)
    return 'No se pudieron pedir los catálogos de cobro (' + esc(ERROR_CATALOGOS) +
      '). <b>No es que no estén configurados: es que no se pudieron pedir.</b> ' +
      'Cambiá de pestaña y volvé, o recargá con Ctrl+F5.';
  if (ERROR_MONEDAS_COBRO)
    return 'No se pudieron leer las monedas (' + esc(ERROR_MONEDAS_COBRO) + '). ' +
      '<b>No es que no haya: es que no se pudieron leer.</b> Cambiá de pestaña y ' +
      'volvé, o recargá con Ctrl+F5.';
  return '';
}

//  `frenoPlata()`: COBRAR (panel del pedido, pago a cuenta, cobro en la calle);
//  además necesita el medio y la moneda en que está expresado lo que falta.
export function frenoPlata(){
  //  El rechazo primero, igual que en `frenoMonedas()`.
  if (ERROR_CATALOGOS) return frenoMonedas();
  if (ERROR_MONEDA_CAT)
    return 'No se pudo leer en qué moneda está expresada la plata del sistema (' +
      esc(ERROR_MONEDA_CAT) + '). <b>No es que no esté configurada: es que no se ' +
      'pudo leer.</b> Hasta que se lea, cualquier monto que salga de acá podría ' +
      'estar en otra moneda. Cambiá de pestaña y volvé, o recargá con Ctrl+F5.';
  const monedas = frenoMonedas();
  if (monedas) return monedas;
  if (ERROR_MEDIOS)
    return 'No se pudieron leer los medios de pago (' + esc(ERROR_MEDIOS) + '). ' +
      '<b>No es que no haya: es que no se pudieron leer.</b> Cambiá de pestaña y ' +
      'volvé, o recargá con Ctrl+F5.';
  return '';
}

//  La puerta más chica: cobrar en la moneda de catálogo no necesita cotización;
//  en otra sí. Trabar todo el cobro sería cerrar de más (regla 11). El cartel
//  dice "no se pudo leer", no "no hay cotización".
export const frenoCotizacion = (cod) =>
  ERROR_COTIZ_COBRO && cod && cod !== MONEDA_CAT
    ? 'No se pudieron leer las cotizaciones (' + esc(ERROR_COTIZ_COBRO) + '), así ' +
      'que no se sabe cuánto vale hoy ' + esc(cod) + '. <b>No es que no esté ' +
      'cargada: es que no se pudo leer.</b> En ' + esc(MONEDA_CAT) + ' se puede ' +
      'cobrar igual.'
    : '';

//  Lo mismo que hace `app.a_usd` en la base, pero acá sólo para mostrar
//  el equivalente mientras se tipea. El número que vale es el que estampa
//  la base cuando el cobro entra.
export function aCatalogo(monto, codigoMoneda){
  const a = COTIZ[codigoMoneda], b = COTIZ[MONEDA_CAT];
  if (!Number.isFinite(monto) || !a || !b) return null;
  return Math.round(monto * a / b * 100) / 100;
}
export function desdeCatalogo(montoCat, codigoMoneda){
  const a = COTIZ[codigoMoneda], b = COTIZ[MONEDA_CAT];
  if (!Number.isFinite(montoCat) || !a || !b) return null;
  //  Hacia abajo: la base rechaza cobrar de más, y redondear para arriba
  //  dejaría el botón trabado por un centavo.
  const v = Math.floor(montoCat * b / a * 100) / 100;
  //  Salvo que lo que falte sea tan poco que hacia abajo dé cero: ahí el
  //  saldo nunca se podría cerrar. La base tolera un centavo de más.
  return (v === 0 && montoCat > 0) ? 0.01 : v;
}

const CHIP_COBRO = { cobrado:'c-ok', parcial:'c-warn', sin_cobrar:'c-pen', a_favor:'c-bad' };
const TEXTO_COBRO = { cobrado:'cobrado', parcial:'parcial',
                      sin_cobrar:'sin cobrar', a_favor:'cobrado de más' };

export function celdaCobro(p){
  if (!Number(p.a_cobrar || 0) && !Number(p.cobrado || 0))
    return '<span class="hint">—</span>';
  const est = p.estado_cobro || 'sin_cobrar';
  const saldo = Number(p.saldo || 0);
  const chip = `<span class="chip ${CHIP_COBRO[est]||'c-pen'}">${TEXTO_COBRO[est]||esc(est)}</span>`;
  const falta = Math.abs(saldo) > 0.005
    ? `<div class="hint">falta ${num(saldo)} ${esc(rotuloMoneda())}</div>` : '';
  const btn = puede('caja','operar')
    ? ` <button class="b b-sec" data-cobro="${p.id}">Cobrar</button>`
    : ` <button class="b b-sec" data-cobro="${p.id}">Ver</button>`;
  return chip + btn + falta;
}

//  El panel de cobro: se abre debajo de la fila del pedido, como el de
//  cambiar la entrega.
$('#tblPedidos').addEventListener('click', async e => {
  const b = el(e.target).closest('[data-cobro]');
  if (!b) return;
  const p = PEDIDOS.find(x => x.id === Number(b.dataset.cobro));
  if (!p) return;
  const fila = b.closest('tr');
  if (fila.nextElementSibling?.id === 'filaCobro'){ fila.nextElementSibling.remove(); return; }
  document.getElementById('filaCobro')?.remove();
  document.getElementById('filaEntrega')?.remove();

  const tr = document.createElement('tr');
  tr.id = 'filaCobro';
  tr.innerHTML = `<td colspan="8"><div class="confirmar" id="panelCobro">
      <div class="conf-tit">Cobros del pedido ${esc(p.codigo)}</div>
      <div class="hint" id="coCarga">Buscando…</div>
    </div></td>`;
  fila.after(tr);
  await pintarPanelCobro(p);
});

async function pintarPanelCobro(p){
  const panel = document.getElementById('panelCobro');
  if (!panel) return;

  const [{ data: saldo, error: eS }, { data: cobros, error: eC }] = await Promise.all([
    rpcLee('saldo_pedido', { p_pedido_id: p.id }),
    pedir(sb.from('v_cobros').select('*').eq('pedido_id', p.id).order('ocurrido_en')),
    //  En qué caja cae el cobro de ESTE pedido y si la puedo operar (D93): la
    //  decide el tipo de entrega, como `app.caja_del_pedido` (clave del tipo, o
    //  `default`). El cobro de la CALLE (`p_vuelta_id`) no toca caja: éste es el de mostrador.
    misCajas()
  ]);
  if (eS){
    //  Ojo: el panel se reemplaza entero; hay que volver a poner el botón de
    //  cerrar y `#coMsg`. Sin él, `refrescarCobros` escribe el "Cobro
    //  registrado" sobre `null` y queda sólo un cartel rojo (y se cobra dos veces).
    panel.innerHTML = `<div class="aviso a-bad">${esc(eS.message)}</div>
      <div class="fila-acc"><button class="b b-sec" id="coCerrar">Cerrar</button></div>
      <div id="coMsg"></div>`;
    $('#coCerrar', panel).onclick = () =>
      document.getElementById('filaCobro')?.remove();
    return;
  }

  const falta = Number(saldo?.saldo ?? 0);
  //  La puerta se decide acá, no adentro del formulario. Sin poder leer los
  //  catálogos el formulario no se dibuja (si no, `codMoneda()` revienta antes
  //  de enganchar el botón y "Registrar cobro" queda muerto sin cartel).
  const freno = frenoPlata();
  const acto = 'cobro:' + (p.tipo_entrega || 'default');
  //  Sólo un `false` cierra la puerta; con null (no se sabe) el formulario se
  //  dibuja igual: la puerta la tiene la base.
  const cajaMia = puedoActo(acto) !== false;
  const puedeCobrar = puede('caja','operar') && falta > 0.005 && !freno && cajaMia;
  //  El cartel se muestra sólo a quien iba a cobrar.
  const frenado = puede('caja','operar') && falta > 0.005 && !!freno;
  //  El otro motivo para no dibujarlo, y se dice distinto (D87): esta plata
  //  cae en una caja que no estoy habilitado a operar.
  const ajeno = puede('caja','operar') && falta > 0.005 && !freno && !cajaMia;

  const filas = (cobros||[]).map(c => `
    <tr data-cobrofila="${c.id}"${c.anulado || c.es_anulacion ? ' style="opacity:.55"' : ''}>
      <td class="hint">${new Date(c.ocurrido_en).toLocaleString('es-AR')}</td>
      <td>${esc(c.medio_pago)}</td>
      <td class="num">${esc(c.moneda)} ${num(Number(c.monto))}${
        Number(c.vuelto) > 0 ? ` <span class="hint">(vuelto ${num(Number(c.vuelto))})</span>` : ''}</td>
      <td class="num">${num(Number(c.monto_usd ?? 0))}</td>
      <td>${celdaCajaDelCobro(c)}</td>
      <td>${esc(c.cobro || '—')}</td>
      <td>${c.es_anulacion ? '<span class="chip c-bad">anulación</span>'
            : c.anulado ? '<span class="chip c-bad">anulado</span>'
            : botonAnular(c)}
          ${c.motivo ? `<div class="hint">${esc(c.motivo)}</div>` : ''}</td>
    </tr>`).join('');

  //  Segunda etapa de legibilidad (D181): la cuenta en UNA frase, y la
  //  explicación plegada en «¿Cómo funciona?».
  panel.innerHTML = `
    <div class="conf-tit">Cobros del pedido ${esc(p.codigo)}</div>
    <p class="conf-txt">
      A cobrar <b>${num(Number(saldo?.a_cobrar ?? 0))} ${esc(rotuloMoneda())}</b> ·
      cobrado <b>${num(Number(saldo?.cobrado ?? 0))}</b> ·
      falta <b>${num(falta)}</b>.</p>
    ${eC ? '' : `<details class="como"><summary>¿Cómo funciona?</summary>
      <p class="hint">Un cobro no se edita ni se borra: se anula, y la anulación
      queda a la vista. Lo que cobró una moto en la calle lo tiene la moto hasta
      que lo rinde: anularlo antes baja lo que tiene que rendir; después, la plata
      sale de la caja donde se rindió.</p></details>`}
    ${eC ? `<div class="aviso a-bad">${esc(eC.message)}</div>` : `
    <div class="tw" style="max-height:12.941rem;margin-bottom:0.588rem"><table>
      <thead><tr><th>Cuándo</th><th>Medio</th><th class="num">Monto</th>
                 <th class="num">${esc(rotuloMoneda())}</th><th>Caja</th><th>Quién cobró</th><th></th></tr></thead>
      <tbody>${filas || '<tr><td colspan="7" class="vacio">Todavía no se cobró nada</td></tr>'}</tbody>
    </table></div>`}
    ${puedeCobrar ? `
    <div class="grid g4">
      <label>Medio de pago <select id="coMedio">${
        MEDIOS.map(m => `<option value="${m.id}">${esc(m.nombre)}</option>`).join('')}</select></label>
      <label>Moneda <select id="coMoneda">${
        MONEDAS.map(m => `<option value="${m.id}" data-cod="${esc(m.codigo)}"${
          m.codigo === MONEDA_CAT ? ' selected' : ''}>${esc(m.codigo)}</option>`).join('')}</select></label>
      <label>Monto <input id="coMonto" type="number" step="0.01" min="0"></label>
      <label>Con cuánto paga <span class="hint">opcional</span>
        <input id="coRecibido" type="number" step="0.01" min="0" placeholder="para el vuelto"></label>
    </div>
    <div class="grid" style="margin-top:0.471rem">
      <label>Motivo <span class="hint">opcional</span>
        <input id="coMotivo" placeholder="Ej. seña, o quién trajo la plata"></label>
    </div>
    <p class="hint" id="coEquiv" style="margin:0.471rem 0"></p>
    <div id="coCotMsg"></div>
    <div class="fila-acc">
      <button class="b b-sec" id="coCerrar">Cerrar</button>
      <button class="b b-pri" id="coOk">Registrar cobro</button>
    </div>` : `
    ${ajeno ? `<div class="aviso a-warn">${noHabilitado(cajaDelActo(acto), 'cobrar en')}
      El cobro de este pedido cae en esa caja porque así está configurado para
      su tipo de entrega. Lo puede registrar alguien habilitado en ella.</div>` : ''}
    ${frenado ? `<div class="aviso a-bad">${freno}<br>Mientras tanto no se puede
      registrar ningún cobro de este pedido: la plata que entra se guarda con su
      medio y su moneda, y ése es el dato que falta. El cobro no se perdió: se
      registra cuando esto se lea.</div>` : ''}
    <div class="fila-acc"><button class="b b-sec" id="coCerrar">Cerrar</button></div>`}
    <div id="coMsg"></div>`;

  $('#coCerrar', panel).onclick = () =>
    document.getElementById('filaCobro')?.remove();

  $$('[data-anular]', panel).forEach(x => x.onclick = () => {
    const c = (cobros||[]).find(k => k.id === Number(x.dataset.anular));
    if (c) abrirAnulacion(c, { fila: x.closest('tr'), columnas: 7,
                               alListo: m => refrescarCobros(p, m) });
  });
  if (!puedeCobrar) return;

  //  Con `?.`: un select vacío no puede tirar TypeError acá, o el botón de
  //  cobrar quedaría sin handler y sin explicación.
  const codMoneda = () => $('#coMoneda', panel).selectedOptions[0]?.dataset.cod;
  const refrescar = () => {
    const cod = codMoneda();
    const monto = Number($('#coMonto', panel).value);
    const eq = aCatalogo(monto, cod);
    const rec = Number($('#coRecibido', panel).value);
    const vuelto = $('#coRecibido', panel).value !== '' && rec >= monto
      ? Math.round((rec - monto) * 100) / 100 : null;
    //  Sólo la rama de una moneda sin cotización legible se traba; en la de
    //  catálogo el cobro sigue andando.
    const cot = frenoCotizacion(cod);
    $('#coOk', panel).disabled = !!cot;
    if (cot) avisoHtml('#coCotMsg','bad', cot);
    else limpiar('#coCotMsg');
    $('#coEquiv', panel).innerHTML = !monto ? ''
      : (cot ? ''
          : eq === null
          //  DOS motivos distintos: si la lectura se cayó, la cotización está
          //  cargada y la base NO rechaza nada; no decir lo contrario.
          ? (ERROR_COTIZ_COBRO
              ? `<span class="chip c-warn">No se pudieron leer las cotizaciones, así que no se puede mostrar el equivalente. El cobro en ${esc(cod)} entra igual.</span>`
              : `<span class="chip c-bad">No hay cotización de ${esc(cod)} cargada: la base va a rechazar el cobro.</span>`)
          : `Equivale a <b>${num(eq)} ${esc(rotuloMoneda())}</b>` +
            (eq > falta + 0.005 ? ` — <span class="chip c-bad">es más que lo que falta (${num(falta)})</span>` : '') +
            (vuelto !== null ? ` · vuelto <b>${esc(cod)} ${num(vuelto)}</b>` : ''));
  };
  const prellenar = () => {
    const v = desdeCatalogo(falta, codMoneda());
    $('#coMonto', panel).value = v === null ? '' : String(v);
    refrescar();
  };
  $('#coMoneda', panel).onchange = prellenar;
  $('#coMonto', panel).oninput = refrescar;
  $('#coRecibido', panel).oninput = refrescar;
  prellenar();

  $('#coOk', panel).onclick = async ev => {
    const btn = el(ev.currentTarget);
    limpiar('#coMsg');
    const monto = Number($('#coMonto', panel).value);
    if (!(monto > 0)){ aviso('#coMsg','bad','El monto tiene que ser mayor que cero.'); return; }
    const recTxt = $('#coRecibido', panel).value;
    btn.disabled = true;
    const { data, error } = await rpc('registrar_cobro', {
      p_pedido_id    : p.id,
      p_medio_pago_id: Number($('#coMedio', panel).value),
      p_moneda_id    : Number($('#coMoneda', panel).value),
      p_monto        : monto,
      p_recibido     : recTxt === '' ? null : Number(recTxt),
      p_motivo       : $('#coMotivo', panel).value.trim() || null
    });
    //  Si la base no contestó, el cobro quizá quedó registrado: el botón no
    //  vuelve (D91, D92). Lo trae de nuevo el panel repintado.
    if (!noContesto(error)) btn.disabled = false;
    if (error){ aviso('#coMsg','bad', error.message); return; }
    await refrescarCobros(p, data.mensaje);
  };
}

//  DÓNDE ESTÁ LA PLATA DE UN COBRO (D182). Un cobro de la calle (`vuelta_id`)
//  no entra a ninguna caja hasta que la moto lo rinde: la columna «Caja» dice
//  eso y no «sin sesión», que es un aviso del mostrador (la reversa de uno a
//  rendir tampoco tiene sesión, y no le falta nada). `vuelta` y `motoquero`
//  llegan null a quien no ve esa vuelta (`vuelta.ver`, D84): la pantalla dice
//  que no la ve en vez de callar.
const fechaCorta = (t: string) => new Date(t).toLocaleDateString('es-AR', { day: 'numeric', month: 'numeric' });
export function celdaCajaDelCobro(c: any){
  if (!c.vuelta_id)
    return `${esc(c.caja || '—')}${c.sesion_id ? '' : ' <span class="chip c-warn">sin sesión</span>'}`;
  //  Un cobro anulado antes de rendir y su anulación netean cero: no hay nada
  //  que rendir, y decir «a rendir» sería afirmar plata que nadie trae.
  const chip = c.rendicion_id
    ? `<span class="chip c-ok">rendido${c.rendido_en ? ' el ' + fechaCorta(c.rendido_en) : ''}</span>`
    : c.anulado || c.es_anulacion
    ? '<span class="chip c-pen">anulado antes de rendir</span>'
    : '<span class="chip c-warn">en la calle · a rendir</span>';
  const vuelta = c.vuelta == null
    ? 'vuelta que no podés ver'
    : `vuelta ${esc(c.vuelta)}${c.motoquero ? ' · ' + esc(c.motoquero) : ''}`;
  return `${chip}<div class="hint">${vuelta}</div>`;
}

//  QUIÉN PUEDE ANULARLO (D182): las tres ramas de `app.anular_cobro`.
//  · mostrador: `caja.operar` y la habilitación de la caja del MOVIMIENTO (un
//    `caja_id` null es «no se sabe»: se ofrece y contesta la base);
//  · de la calle, a rendir: quien recibe las rendiciones;
//  · de la calle, ya rendido: `caja.operar` y la habilitación de la caja de la
//    RENDICIÓN (null si la rendición no se ve: se ofrece, como arriba).
//  El nombre de la caja sale de `mis_cajas` (security definer) porque
//  `v_cobros.caja` no llega sin `caja.ver`.
export function botonAnular(c: any){
  const boton = `<button class="b b-sec" data-anular="${c.id}">Anular</button>`;
  if (c.vuelta_id && !c.rendicion_id)
    return puede('rendicion','recibir') ? boton
      : '<span class="hint">Lo anula quien recibe las rendiciones</span>';
  if (!puede('caja','operar')) return '';
  const caja = c.vuelta_id ? c.rendicion_caja_id : c.caja_id;
  return puedoOperar(caja) !== false ? boton
    : `<span class="hint">${noHabilitado(
        (c.vuelta_id ? null : c.caja) ?? nombreCaja(caja), 'anular un cobro de')}</span>`;
}

//  LA FILA DE CONFIRMACIÓN (D182; antes era un `prompt()`). Dice ANTES de qué
//  lado sale la plata, pide el motivo y deja «Sí, anular» al final. La usan los
//  cobros del pedido y la tarjeta «Corregir un cobro de la calle» de
//  Rendiciones: `alListo` es lo que cada una vuelve a leer después.
export function abrirAnulacion(c: any, op: { fila: Element; columnas: number;
                                            alListo: (mensaje: string) => unknown }){
  document.getElementById('filaAnular')?.remove();
  const plata = `${esc(c.moneda)} ${num(Math.abs(Number(c.monto)))}`;
  const moto = c.motoquero ? esc(c.motoquero) : 'una moto que no podés ver';
  const cajaRend = nombreCaja(c.rendicion_caja_id);
  const explico = !c.vuelta_id
    ? `Vas a anular <b>${plata}</b> cobrado en el mostrador: salen de <b>${
        esc(c.caja ?? nombreCaja(c.caja_id) ?? 'la caja donde entró')}</b>.`
    : !c.rendicion_id
    ? `Vas a anular <b>${plata}</b> cobrado por ${moto} en la calle. <b>La plata la
       tiene ${c.motoquero ? 'él' : 'la moto'}</b>: lo que debe rendir baja.`
    : `Vas a anular <b>${plata}</b> cobrado por ${moto} en la calle, ya rendido:
       salen ${plata} de <b>${esc(cajaRend ?? 'la caja donde se rindió')}</b>.`;
  const tr = document.createElement('tr');
  tr.id = 'filaAnular';
  tr.innerHTML = `<td colspan="${op.columnas}"><div class="confirmar grave">
      <div class="conf-tit">Anular el cobro de ${plata}${c.pedido ? ' · pedido ' + esc(c.pedido) : ''}</div>
      <p class="conf-txt">${explico}</p>
      <div class="grid"><label>Motivo <input id="anMotivo" autocomplete="off"
          placeholder="Por qué se anula (es plata: queda registrado)"></label></div>
      <div class="fila-acc" style="margin-top:0.706rem">
        <button class="b b-sec" id="anCancelar">Cancelar</button>
        <button class="b b-peligro" id="anOk">Sí, anular</button>
      </div>
      <div id="anMsg"></div>
    </div></td>`;
  op.fila.after(tr);
  //  La tabla de cobros tiene un alto tope con su propia barra: abierta la
  //  confirmación, se le saca el tope para que se lea entera sin desplazar.
  const caja = op.fila.closest('.tw') as HTMLElement | null;
  if (caja) caja.style.maxHeight = 'none';
  $('#anMotivo', tr).focus();
  $('#anCancelar', tr).onclick = () => tr.remove();
  $('#anOk', tr).onclick = async ev => {
    const btn = el(ev.currentTarget);
    limpiar('#anMsg');
    const motivo = $('#anMotivo', tr).value.trim();
    if (!motivo){ aviso('#anMsg','bad','Escribí el motivo: es plata y queda registrado.'); return; }
    btn.disabled = true;
    const { data, error } = await rpc('anular_cobro', { p_pago_id: c.id, p_motivo: motivo });
    //  Una anulación que no contestó quizá quedó hecha: el botón no vuelve (D91, D92).
    if (!noContesto(error)) btn.disabled = false;
    if (error){ aviso('#anMsg','bad', error.message); return; }
    tr.remove();
    await op.alListo(data?.mensaje || 'Cobro anulado.');
  };
}

//  IR A LOS COBROS DE UN PEDIDO desde otra pestaña (D183: después de sacar algo
//  de Entregado, la plata se revisa acá). Aprieta la pestaña verdadera, ESPERA
//  la lectura que ese click disparó (otra más se pisaría con ella y borraría
//  el panel recién abierto) y abre el panel de la fila. El filtro de la lista
//  se pone en ESE pedido (un filtro viejo lo escondería); si igual no está
//  (fuera de los 200), lo dice.
export async function irACobros(pedidoId: number, codigo: string){
  $('#buscaPed').value = codigo;
  $('#tabPedidos').click();
  await leyendoPedidos();
  const b = $(`#tblPedidos [data-cobro="${pedidoId}"]`);
  if (b){
    b.click();
    b.closest('tr').scrollIntoView({ block: 'start' });
    return;
  }
  //  Con la lista caída ya lo dice su cartel: no se agrega otro.
  if (!ERROR_PEDIDOS)
    aviso('#pedMsg','warn', pedidosCortados()
      ? `El pedido ${codigo} no está entre los últimos ${num(TOPE_PEDIDOS)} que muestra la lista: sus cobros no se pueden abrir desde acá.`
      : `El pedido ${codigo} no está en esta lista: buscalo y abrí sus cobros.`);
}

//  Después de tocar plata hay que volver a traer el pedido: `a_cobrar`,
//  `cobrado` y `saldo` salen de la base, no de una cuenta hecha acá.
async function refrescarCobros(p, mensaje){
  await cargarPedidos();
  const fila = $(`#tblPedidos [data-cobro="${p.id}"]`)?.closest('tr');
  const nuevo = PEDIDOS.find(x => x.id === p.id) || p;
  if (fila){
    const tr = document.createElement('tr');
    tr.id = 'filaCobro';
    tr.innerHTML = `<td colspan="8"><div class="confirmar" id="panelCobro"></div></td>`;
    fila.after(tr);
    await pintarPanelCobro(nuevo);
    if (mensaje) aviso('#coMsg','ok', mensaje);
  }
  //  Si la fila ya no está, el mensaje NO se pierde (no saber si la plata entró
  //  hace que se cobre dos veces). Ojo: la fila puede faltar por cuatro motivos
  //  (filtro, saldo en cero, fuera de las 200, error de lectura); por eso se
  //  pregunta por `ERROR_PEDIDOS` y no por la ausencia de la fila.
  else if (mensaje && ERROR_PEDIDOS)
    //  Va DENTRO de la tabla, donde está el cartel de "no se pudieron leer los
    //  pedidos"; se va solo en el próximo repintado.
    $('#tblPedidos').insertAdjacentHTML('beforeend',
      `<tr><td colspan="8"><div class="aviso a-ok">${esc(mensaje)} La lista de ` +
      `pedidos no se pudo volver a leer, así que todavía no vas a verlo ` +
      `reflejado acá arriba.</div></td></tr>`);
  else if (mensaje) aviso('#pedMsg','ok', mensaje);
  if ($('#tabCaja') && !$('#tabCaja').classList.contains('oculto')) cargarCaja();
}

//  Los catálogos de cobro los necesitan la Caja y también el motoquero.
//  Se cargan una sola vez por sesión... mientras se hayan podido leer.
//
//  Ojo: no guardar la promesa de una carga que FALLÓ, o Caja, Cuentas, Vueltas
//  y Comisiones quedan sin monedas hasta recargar. Si algo no se pudo leer, el
//  caché se olvida y la próxima pantalla reintenta.
let CATALOGOS_COBRO = null;
export const catalogosCobro = () => (CATALOGOS_COBRO ??= cargarCatalogosCobro()
  .then(() => { ERROR_CATALOGOS = ''; if (frenoPlata() || ERROR_COTIZ_COBRO) CATALOGOS_COBRO = null; })
  //  Si la promesa RECHAZA: se olvida el caché (si no, el rechazo queda para
  //  siempre) y NO se relanza, porque caja, cuentas, comisiones, pedidos y
  //  arranque hacen `await`/`.then` sin `try`. Se convierte en bandera, que
  //  `frenoPlata()` y `frenoMonedas()` miran primero.
  .catch(e => {
    ERROR_CATALOGOS = e?.message || 'no se pudieron pedir los catálogos de cobro';
    CATALOGOS_COBRO = null;
  }));
